import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { authApi } from '../api/auth';
import type { User, FirmStatus, MeResponse } from '../types/auth';

const IS_DEMO = import.meta.env.VITE_DEMO_MODE === 'true';
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const DEMO_SESSION_KEY = 'raporgor.demo.session';

interface AuthContextValue {
  isAuthenticated: boolean;
  isDemo: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  firmStatus: FirmStatus | null;
  // Demo: login() no-args. Real-firm: login(email, password). Single
  // overload accepts optional credentials so call sites can be uniform.
  login: (email?: string, password?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [firmStatus, setFirmStatus] = useState<FirmStatus | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initial restore — JWT from localStorage (real) or session flag (demo).
  useEffect(() => {
    const init = async () => {
      if (IS_DEMO) {
        setIsAuthenticated(sessionStorage.getItem(DEMO_SESSION_KEY) === 'active');
        setIsLoading(false);
        return;
      }
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      if (!storedToken || !storedUser) {
        setIsLoading(false);
        return;
      }

      const applySession = (activeToken: string, me: MeResponse) => {
        setUser(me.user);
        setToken(activeToken);
        setIsAuthenticated(true);
        setFirmStatus(
          me.firmStatus
            ? {
                status: me.firmStatus as FirmStatus['status'],
                deactivationReason: me.deactivationReason,
                licenseExpiresAt: me.licenseExpiresAt,
              }
            : null,
        );
        localStorage.setItem(USER_KEY, JSON.stringify(me.user));
      };

      const clearSession = () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      };

      try {
        applySession(storedToken, await authApi.getMe(storedToken));
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401) {
          // Access token süresi dolmuş — refresh token ile sessizce yenile.
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          try {
            if (!refreshToken) throw err;
            const refreshed = await authApi.refresh(refreshToken);
            localStorage.setItem(TOKEN_KEY, refreshed.token);
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshed.refreshToken);
            applySession(refreshed.token, await authApi.getMe(refreshed.token));
          } catch {
            clearSession();
          }
        } else if (status !== undefined) {
          // Diğer HTTP hataları (403 vb.) — oturum geçersiz say.
          clearSession();
        } else {
          // Ağ hatası/transient — kullanıcıyı atma, cache'lenmiş oturumla devam et.
          setUser(JSON.parse(storedUser) as User);
          setToken(storedToken);
          setIsAuthenticated(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const login = useCallback(async (email?: string, password?: string) => {
    if (IS_DEMO) {
      sessionStorage.setItem(DEMO_SESSION_KEY, 'active');
      setIsAuthenticated(true);
      return;
    }
    if (!email || !password) {
      throw new Error('E-posta ve şifre gerekli');
    }
    const resp = await authApi.login(email, password);
    localStorage.setItem(TOKEN_KEY, resp.token);
    localStorage.setItem(USER_KEY, JSON.stringify(resp.user));
    if (resp.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, resp.refreshToken);
    }
    setToken(resp.token);
    setUser(resp.user);
    setIsAuthenticated(true);
    setFirmStatus(
      resp.firmStatus
        ? {
            status: resp.firmStatus as FirmStatus['status'],
            deactivationReason: resp.deactivationReason,
            licenseExpiresAt: resp.licenseExpiresAt,
          }
        : null,
    );
  }, []);

  const logout = useCallback(() => {
    if (IS_DEMO) {
      sessionStorage.removeItem(DEMO_SESSION_KEY);
    } else {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        // Fire-and-forget: sunucudaki refresh token'ı iptal et.
        void authApi.logout(refreshToken).catch(() => undefined);
      }
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    setUser(null);
    setToken(null);
    setFirmStatus(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isDemo: IS_DEMO,
        isLoading,
        user,
        token,
        firmStatus,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
